/**
 * endpointCounters
 * ──────────────────────────────────────────────────────────────────────
 * Tiny in-memory counter for scheduler-relevant HTTP responses. Used by
 * the `/echo/scheduler/_trace` debug page to show ops which endpoints
 * are degraded (404 / 5xx / 0-network) since the page was loaded.
 *
 * Counters are bucketed by status family per path:
 *   `200` (any 2xx)
 *   `404`
 *   `5xx` (500-599)
 *   `0`   (network failure / status 0)
 *   `other` (3xx, 4xx other than 404)
 *
 * Counters live in module scope — they reset on a hard refresh, which
 * is the desired behaviour for an ops debug page (it shows the current
 * session's degradation, not historical).
 *
 * NO PHI is ever stored — only the URL path and an integer count.
 * Query strings + headers + bodies are stripped before bumping.
 */
const counters = new Map();

// localStorage persistence — opt-in via SchedulerTraceDebug toggle.
// Key is namespaced + versioned so a future schema change won't crash
// on stale data. Stored payload is ONLY counters (path × buckets) —
// matches the in-memory contract; no PHI.
const LS_KEY = 'echo:scheduler:trace_counters:v1';
const LS_ENABLED_KEY = 'echo:scheduler:trace_counters:enabled:v1';

const lsAvailable = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    window.localStorage.setItem('__t', '__t');
    window.localStorage.removeItem('__t');
    return true;
  } catch { return false; }
};

const lsEnabled = () => {
  if (!lsAvailable()) return false;
  return window.localStorage.getItem(LS_ENABLED_KEY) === '1';
};

const hydrateFromLs = () => {
  if (!lsAvailable() || !lsEnabled()) return;
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return;
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object') return;
    Object.entries(data).forEach(([k, v]) => {
      if (v && typeof v === 'object') counters.set(k, v);
    });
  } catch { /* corrupt cache, ignore */ }
};

const persistToLs = () => {
  if (!lsAvailable() || !lsEnabled()) return;
  try {
    const out = {};
    for (const [k, v] of counters.entries()) out[k] = v;
    window.localStorage.setItem(LS_KEY, JSON.stringify(out));
  } catch { /* quota / write failed, skip */ }
};

// Hydrate on module load so a navigation between routes preserves
// counters when persistence is enabled.
hydrateFromLs();

export const setEndpointCountersPersistence = (on) => {
  if (!lsAvailable()) return;
  if (on) {
    window.localStorage.setItem(LS_ENABLED_KEY, '1');
    persistToLs();  // flush current in-memory state
  } else {
    window.localStorage.removeItem(LS_ENABLED_KEY);
    window.localStorage.removeItem(LS_KEY);
  }
};
export const isEndpointCountersPersisted = () => lsEnabled();

const stripPath = (urlOrPath) => {
  if (!urlOrPath) return '';
  // Strip protocol + host + querystring + trailing slashes.
  const s = String(urlOrPath).split('?')[0].split('#')[0];
  try {
    const u = new URL(s, 'http://x');
    return u.pathname.replace(/\/+$/g, '');
  } catch {
    return s.replace(/\/+$/g, '');
  }
};

const bucket = (status) => {
  if (status >= 200 && status < 300) return '200';
  if (status === 404)                return '404';
  if (status >= 500 && status < 600) return '5xx';
  if (!status)                       return '0';
  return 'other';
};

/** Bump the counter for a path × status. Call from the http client. */
export const bumpEndpoint = (urlOrPath, status) => {
  const path = stripPath(urlOrPath);
  // Only track scheduler-relevant paths (keeps the debug page tight).
  if (!path.startsWith('/api/scheduler/')) return;
  const key = path;
  if (!counters.has(key)) {
    counters.set(key, { '200': 0, '404': 0, '5xx': 0, '0': 0, 'other': 0, total: 0, lastStatus: null, lastAt: null });
  }
  const c = counters.get(key);
  c[bucket(status)] += 1;
  c.total += 1;
  c.lastStatus = status;
  c.lastAt = new Date().toISOString();
  // Throttle would be nicer for perf, but counters are scheduler-only
  // (already filtered) and the writes are < 1KB each. Keep it simple.
  persistToLs();
};

/** Snapshot of the counters, sorted by `total` descending. */
export const getEndpointCounters = () => {
  return Array.from(counters.entries())
    .map(([path, c]) => ({ path, ...c }))
    .sort((a, b) => b.total - a.total);
};

/** Reset (used by the debug page's reset button). */
export const resetEndpointCounters = () => {
  counters.clear();
  persistToLs();
};
